/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import VimeoPlayer from '@vimeo/player';
import { AbstractVideo } from './abstract-video';
import { Player } from './player';
import { urlHelper } from 'amb/helpers/url.helper';

/**
 * Vimeo model
 */
export class Vimeo extends AbstractVideo {
  static TYPE = 'vimeo';
  static PARAMS = ['id', 'url', 'autopause', 'autoplay', 'background', 'byline', 'color', 'controls', 'height', 'loop', 'maxheight', 'maxwidth', 'muted', 'playsinline', 'portrait', 'speed', 'title', 'transparent', 'width'];
  static EVENTS = ['play', 'playing', 'pause', 'ended', 'timeupdate', 'progress', 'seeking', 'seeked', 'texttrackchange', 'chapterchange', 'cuechange', 'cuepoint', 'volumechange', 'playbackratechange', 'bufferstart', 'bufferend', 'error', 'loaded', 'durationchange', 'fullscreenchange', 'qualitychange', 'camerachange', 'resize'];
  static ID_PATTERN = /(?:^|\/|=|:)(\d+(?:\/\w+)?)(?:\?|&|\/|"|,|$)/;
  static TYPE_PATTERN = /vimeo|^\d+(?:\/\w+)?(?:\?|,|$)/;

  /**
   * @protected
   */
  get oembedUrl() {
    const videoUrl = `https://vimeo.com/${this.id}`;
    const oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(videoUrl)}`;

    return urlHelper.appendParams(oembedUrl, this.options.params);
  }

  /**
   * Create new Vimeo player instance for the given response metadata
   *
   * @protected
   *
   * @param {object} metadata Vimeo metadata object, see [Vimeo: Working with oEmbed](https://developer.vimeo.com/api/oembed/videos#embedding-a-video-with-oembed-step-1)
   *
   * @returns {Player} Returns new Vimeo player instance
   */
  createPlayer(metadata) {
    const div = document.createElement('div');

    div.innerHTML = metadata.html.replace(' allowfullscreen ', ' ');
    this.applyClip(div.firstChild);
    this.element.appendChild(div.firstChild);
    this.player = new Player(new VimeoPlayer(this.element, this.options.params), this.constructor.TYPE, this.options.params);

    this.player.player?.element?.addEventListener('load', () => {
      setTimeout(() => this.player.isReady = true, 100);
    });

    this.element.player = this.player;
    return this.player;
  }

  /**
   * Register video events configured in options
   *
   * @protected
   *
   * @param {Player} player The Vimeo player instance
   *
   * @returns {Player} Returns Vimeo player instance
   */
  registerEvents(player) {
    super.registerEvents(player);

    if (typeof this.options.onReady === 'function') {
      player.player.ready().then((...args) => this.options.onReady(this, ...args));
    }

    return player;
  }
}
